import { useState } from 'react';

import { BackrsIcon, appColors } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import { DocumentType } from '@backrs_ui/grow-ui/src/models/models';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { SxProps, Theme, Box, Button, Input, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

import { EnterButton } from './EnterButton';

const { fileUpload } = appStrings.modules.chatBox;

type UploadDocumentParams = Readonly<{
  documentType: DocumentType;
  isCoach?: boolean;
}>;

export const getUploadDocumentMessage = ({ documentType, isCoach }: UploadDocumentParams) => {
  switch (documentType) {
    case 'resume':
      return isCoach ? fileUpload.askYourStudentToUploadResume : fileUpload.uploadResume;
    case 'transcript':
      return isCoach ? fileUpload.askYourStudentToUploadTranscript : fileUpload.uploadTranscript;
    default:
      return '';
  }
};

type FileUploadProps = Readonly<{
  onSubmit: (fileName: string, content?: string) => void;
  onError?: () => void;
  cancelComponent?: React.ReactNode;
  addToProfile?: boolean;
  documentType?: DocumentType;
}>;

type DocumentExtension = 'pdf' | 'docx' | 'txt';

type MimeTypeConfig = Readonly<{
  type: DocumentExtension;
  extension: string;
  mimeTypes: string[];
}>;

const FILE_CONFIG: Record<DocumentExtension, MimeTypeConfig> = {
  pdf: {
    type: 'pdf',
    extension: '.pdf',
    mimeTypes: ['application/pdf'],
  },
  docx: {
    type: 'docx',
    extension: '.docx',
    mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  txt: {
    type: 'txt',
    extension: '.txt',
    mimeTypes: ['text/plain'],
  },
} as const;

export const FileUpload = ({
  onSubmit,
  onError,
  cancelComponent,
  addToProfile,
  documentType,
}: FileUploadProps) => {
  const [selectedFileName, setSelectedFileName] = useState('');
  const [isLoading, setIsUploading] = useState(false);
  const [isAttachingToProfile, setIsAttachingToProfile] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const accessToken = getBackrsToken();

  const allowedTypes = Object.values(FILE_CONFIG).flatMap((config) => config.mimeTypes);
  const allowedExtensions = Object.values(FILE_CONFIG).map((config) => config.extension);

  const userId = useAppSelector(selectUserId);

  const getDocumentType = (mimeType: string): DocumentExtension => {
    return (
      Object.values(FILE_CONFIG).find((config) => config.mimeTypes.includes(mimeType))?.type ??
      'txt'
    );
  };

  const attachDocumentToProfile = async (file: File) => {
    const mediaPayload = {
      type: getDocumentType(file.type),
      name: file.name,
      size: file.size.toString(),
    };

    const mediaResponse = await fetch(`${ENVIRONMENT_URL}/media`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(mediaPayload),
    });

    if (!mediaResponse.ok) {
      throw new Error('Failed to get upload URL');
    }

    const mediaData = await mediaResponse.json();

    const uploadResponse = await fetch(mediaData.upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/pdf',
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    const profilePayload = {
      user_id: userId,
      category_id: 'story',
      visibility: 'panel', // assume we don't want it to default to public
      question_id: 'document',
      option_id: documentType,
      title: file.name, // use the file name as the title since we don't ask for a title
      media_id: mediaData.id,
      media_type: getDocumentType(file.type),
    };

    const profileResponse = await fetch(`${ENVIRONMENT_URL}/users-profile-answers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(profilePayload),
    });

    if (!profileResponse.ok) {
      throw new Error('Failed to create profile answer');
    }
  };

  const stringifyDocumentForLubav = async (file: File) => {
    const formData = new FormData();

    formData.append('file', file);
    const content = await axios.post('/api/document-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    setUploadResponse(content.data);
  };

  const resetState = () => {
    setSelectedFileName('');
    setIsUploading(false);
    setUploadResponse('');
    setUploadedFile(null);
  };

  const processFile = async (file: File) => {
    if (!file) return;

    setUploadedFile(file);
    setSelectedFileName(file.name);
    setIsUploading(true);

    try {
      await stringifyDocumentForLubav(file);
    } catch (error) {
      console.error('Error processing document:', error);

      resetState();
      onError?.();
    } finally {
      setIsUploading(false);
    }
  };

  const preventDefaults = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    preventDefaults(e);
    const file = e.dataTransfer.files[0];

    if (file) {
      const fileExtension = '.' + (file.name.split('.').pop() || file.name).toLowerCase();

      if (allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension)) {
        processFile(file);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) processFile(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
    preventDefaults(e);
    e.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    preventDefaults(e);
    e.currentTarget.classList.remove('dragover');
  };

  const handleSubmit = async () => {
    if (selectedFileName && uploadResponse && uploadedFile) {
      if (addToProfile) {
        setIsAttachingToProfile(true);
        try {
          await attachDocumentToProfile(uploadedFile);
        } finally {
          setIsAttachingToProfile(false);
        }
      }

      onSubmit(selectedFileName, uploadResponse);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={fileUploadContainerStyle} onKeyPress={handleKeyPress}>
      <Box sx={fileUploadButtonContainerStyle}>
        <Button
          component="label"
          sx={fileUploadButtonStyle}
          onDragEnter={preventDefaults}
          onDragLeave={preventDefaults}
          onDragOver={preventDefaults}
          onDrop={handleDrop}
        >
          <BackrsIcon name="IconUpload" alt="Upload" style={{ marginRight: '8px' }} />
          {selectedFileName || fileUpload.uploadAFile}
          <Input
            type="file"
            onChange={handleFileChange}
            inputProps={{ accept: allowedExtensions.join(',') }}
            sx={{ display: 'none' }}
          />
        </Button>
      </Box>

      <Box
        sx={dragAndDropStyle}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={preventDefaults}
        onDrop={(e) => {
          e.currentTarget.classList.remove('dragover');
          handleDrop(e);
        }}
      >
        {selectedFileName ? (
          <Typography sx={{ fontSize: '10pt' }}>
            {isLoading ? fileUpload.uploading : selectedFileName}
          </Typography>
        ) : (
          <Box>
            <BackrsIcon
              name="IconPlusWhiteBlueCircle"
              alt="Upload"
              style={{ marginRight: '8px' }}
            />
            <Typography sx={{ fontSize: '10.5pt' }}>{fileUpload.dragAndDrop}</Typography>
            <Typography sx={dragAndDropDescriptionStyle}>{fileUpload.PDForDOCorTXT}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={submitContainerStyle}>
        <EnterButton
          onClick={handleSubmit}
          label={fileUpload.submit}
          disabled={!selectedFileName || isLoading || isAttachingToProfile}
          showEnterHint
        />
        {cancelComponent}
      </Box>
    </Box>
  );
};
const fileUploadContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 2,
  animation: `${keyframes`
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `} 0.3s ease-out`,
} as const;

const fileUploadButtonStyle: SxProps<Theme> = {
  padding: '6px 16px',
  borderRadius: '8px',
  backgroundColor: appColors.color.blue4,
  color: appColors.color.navy2,
  cursor: 'pointer',
  display: 'flex',
  fontSize: '11pt',
  fontWeight: '600',
  width: 'fit-content',
  justifyContent: 'center',
  textTransform: 'none',
} as const;

const dragAndDropStyle: SxProps<Theme> = {
  border: `2px dashed ${appColors.color.blue1}`,
  borderRadius: '8px',
  padding: '20px',
  height: '130px',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'center',
  display: 'flex',
  textAlign: 'center',
  transition: 'background-color 0.2s ease',
  '&.dragover': {
    backgroundColor: appColors.color.blue4,
  },
};

const dragAndDropDescriptionStyle: SxProps<Theme> = {
  fontSize: '8pt',
  color: appColors.neutral.gray1,
} as const;

const submitContainerStyle: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'space-between',
} as const;

const fileUploadButtonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
} as const;
